import { memo, useEffect, useMemo, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { STATUS_IDS, USER_ROLES_IDS } from '../../../constants/common'
import { useGetLostVehicleStickerIDCardById } from '../../../hooks/permit/query'
import { useLocation } from 'react-router-dom'
import Loader from '../../../components/loader'
import LostVehicleStickerIDCardDetailsCard from '../../../components/card/LostVehicleStickerIDCardDetailsCard'
import useFormWithSchema from '../../../hooks/useFormWithSchema'
import useAuthStore from '../../../store/Auth'
import { APP_ROUTES } from '../../../routes'
import { LostVehicleStickerIDCardSchema } from '../../../schemas/lostVehicleStickerIDCardSchema'
import ActionButtonGroup from './ActionButtonGroup'
import Acknowledgement from './Acknowledgement'
import Header from '../Header'

const LostVehicleStickerIDCard = () => {
    const { t, i18n } = useTranslation();

    const location = useLocation();

    const { user } = useAuthStore()

    const searchParams = new URLSearchParams(location.search);

    const key = searchParams.get("ID");

    const { data: lostVehicleStickerIDCard, isPending: isLostVehicleStickerIDCardLoading } = useGetLostVehicleStickerIDCardById({
        encryptedId: key || "",
    });

    const [isLossDeclaration, setIsLossDeclaration] = useState(false);

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
    } = useFormWithSchema(LostVehicleStickerIDCardSchema);

    useEffect(() => {
        if (!lostVehicleStickerIDCard) {
            setIsLossDeclaration(false);

            setValue("stickerNumber", "");
            setValue("copyNumber", "");

            return
        };

        setValue("stickerNumber", lostVehicleStickerIDCard.s_StickerNumber || "");
        setValue("copyNumber", lostVehicleStickerIDCard.s_CopyNumber || "");
    }, [lostVehicleStickerIDCard, setValue]);

    const isPermitApprovedByNextApprover = useMemo(
        () =>
            !!(
                location.pathname === APP_ROUTES.UPDATE_LOST_VEHICLE_STICKER_ID_CARD &&
                lostVehicleStickerIDCard?.n_CurrentApproverOrderNo &&
                lostVehicleStickerIDCard?.n_CurrentApproverOrderNo > 1
            ),
        [location.pathname, lostVehicleStickerIDCard?.n_CurrentApproverOrderNo]
    );

    const isFieldsDisabled = useMemo(
        () =>
            (user?.n_RoleID !== USER_ROLES_IDS.STAFF &&
                user?.n_RoleID !== USER_ROLES_IDS.ADMIN) ||
            (lostVehicleStickerIDCard && lostVehicleStickerIDCard?.n_ApprovalStatus !== STATUS_IDS.PENDING) ||
            (user?.n_RoleID === USER_ROLES_IDS.STAFF &&
                isPermitApprovedByNextApprover) ||
            (user?.n_RoleID === USER_ROLES_IDS.ADMIN &&
                location.pathname === APP_ROUTES.APPROVE_LOST_VEHICLE_STICKER_ID_CARD),
        [
            isPermitApprovedByNextApprover,
            location.pathname,
            lostVehicleStickerIDCard,
            user?.n_RoleID,
        ]
    );

    return (
        <>
            <Toaster position="top-right" reverseOrder={false} />

            <div className="flex flex-col gap-5">
                <Header title={t("lostVehicleStickerIdCard")} data={lostVehicleStickerIDCard} />

                {key && isLostVehicleStickerIDCardLoading ? (
                    <div className="flex justify-center">
                        <Loader />
                    </div>
                ) : (
                    <>
                        <LostVehicleStickerIDCardDetailsCard
                            control={control}
                            errors={errors}
                            isFieldsDisabled={isFieldsDisabled}
                        />

                        <Acknowledgement
                            isLossDeclaration={isLossDeclaration}
                            setIsLossDeclaration={setIsLossDeclaration}
                            isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
                            isFieldsDisabled={isFieldsDisabled}
                            lostVehicleStickerIDCard={lostVehicleStickerIDCard}
                        />

                        <ActionButtonGroup
                            lostVehicleStickerIDCard={lostVehicleStickerIDCard}
                            handleSubmit={handleSubmit}
                            isFieldsDisabled={isFieldsDisabled}
                            isLossDeclaration={isLossDeclaration}
                            isPermitApprovedByNextApprover={isPermitApprovedByNextApprover}
                        />
                    </>
                )}
            </div>
        </>
    )
}

export default memo(LostVehicleStickerIDCard)
